<i18n>
{
  "fr": {
    "general-compliance-title": "Conformité générale",
    "general-compliance-print": "Imprimer le rapport",
    "compliance-title": "Indicateurs",
    "pending-tasks-label": "Tâches à faire",
    "open-all-drawers": "Tout voir",
    "close-all-drawers": "Fermer les tiroirs"

  },
  "en": {
    "general-compliance-title": "General compliance",
    "general-compliance-print": "Print the report",
    "compliance-title": "Indicators",
    "pending-tasks-label": "Tasks to do",
    "open-all-drawers": "See all",
    "close-all-drawers": "Close all"
  }
}
</i18n>
<template>
  <div v-if="business">
    <prismic-content
      :doctype="$consts.prismic.DOCUMENT_TYPE_GENERAL_COMPLIANCE"
      v-slot="{ content }"
      @loaded="loaded.prismic = true"
    >
      <ui-title>
        {{ $t('general-compliance-title') }}
        <template v-slot:actions>
          <b-button type="is-link" outlined icon-left="print" @click="print">
            {{ $t('general-compliance-print') }}
          </b-button>
        </template>
        <template v-slot:description>
          <prismic-rich-text :field="content.intro" />
        </template>
      </ui-title>

      <div class="card mb-24px is-hidden-print">
        <ui-box>
          <hr class="is-marginless" />
          <div v-for="(drawer, index) of content.drawers" :key="index">
            <ui-drawer>
              <template slot="title">{{ $prismicText(drawer.drawer_title) }}</template>
              <prismic-rich-text class="content is-custom has-max-width-768" :field="drawer.drawer_content" />
            </ui-drawer>
            <hr class="is-marginless" />
          </div>
        </ui-box>
      </div>
    </prismic-content>
    <div class="card">
      <ui-box>
        <div class="level mb-24px">
          <div class="level-left">
            <h2 class="title is-4 has-text-weight-bold is-marginless">
              {{ $t('compliance-title') }}
            </h2>
          </div>
          <div class="level-right is-hidden-print">
            <b-button type="is-link" outlined icon-left="print" @click="print">
              {{ $t('general-compliance-print') }}
            </b-button>
            <b-button
              type="is-link"
              outlined
              icon-left="check-double"
              class="ml-2"
              tag="router-link"
              :to="{ name: $consts.urls.URL_PENDING_TASKS, query: { tab: 'general' } }"
            >
              {{ $t('pending-tasks-label') }}
            </b-button>
            <b-button type="is-link" outlined class="ml-2" @click="toggleAllDrawers()">
              <span v-if="allDrawersOpened">{{ $t('close-all-drawers') }}</span>
              <span v-if="!allDrawersOpened">{{ $t('open-all-drawers') }}</span>
            </b-button>
          </div>
        </div>
        <compliance
          ref="compliance"
          :data="business && business.compliance"
          :loading="$apollo.queries.compliance.loading"
          @drawers-state-updated="onDrawersStateUpdated"
        />
      </ui-box>
    </div>
  </div>
</template>

<script>
import BusinessService from '@/services/business'
import { sendSignal } from '@/services/signals'
import i18n from '@/helpers/i18n'

import Breadcrumbs from '@/mixins/breadcrumbs'

import PrismicContent from '@/components/prismic/prismic-content'
import Compliance from '@/components/indicator/compliance'
import UiBox from '@/components/ui/box'
import UiDrawer from '@/components/ui/drawer'
import UiTitle from '@/components/ui/title'

export default {
  mixins: [Breadcrumbs],
  components: { PrismicContent, Compliance, UiBox, UiDrawer, UiTitle },
  data() {
    return {
      allDrawersOpened: false,
      business: null,
      loaded: {
        prismic: false,
        apollo: false
      }
    }
  },
  computed: {
    businessId() {
      return BusinessService.getCurrentBusiness().id
    },
    breadcrumbs() {
      return [
        {
          label: this.$t('general-compliance-title')
        }
      ]
    }
  },
  apollo: {
    compliance: {
      query() {
        return this.$withFragments(this.$options.query.BusinessCompliance, Compliance.fragments.complianceFields)
      },
      variables() {
        return {
          businessId: this.businessId,
          language: i18n.contentLanguage()
        }
      },
      update(data) {
        this.business = data.business
        this.loaded.apollo = true
      }
    }
  },
  methods: {
    print() {
      window.print()
    },
    toggleAllDrawers() {
      if (!this.allDrawersOpened) {
        this.$refs.compliance.openAll()
        this.allDrawersOpened = true
      } else if (this.allDrawersOpened) {
        this.$refs.compliance.closeAll()
        this.allDrawersOpened = false
      }
    },
    onDrawersStateUpdated({ allOpened }) {
      this.allDrawersOpened = allOpened
    }
  },
  watch: {
    loaded: {
      handler(value) {
        if (value.prismic && value.apollo) {
          sendSignal('scroll')
        }
      },
      deep: true
    }
  }
}
</script>

<graphql>
query BusinessCompliance($businessId: ID!, $language: ContentLanguage!) {
  business(id: $businessId) {
    id
    compliance(language: $language) {
      ...complianceFields
    }
  }
}
</graphql>
